const RULE_AGENT_BASE_URL = process.env.REACT_APP_RULE_AGENT_URL || "http://localhost:9001"

const RULE_AGENT_STREAM_IDLE_TIMEOUT_MS = 60000

export const RESUMO_ALTERACAO_SENTINEL = "RESUMO_ALTERACAO:"

export const PREVIOUS_RULE_ATTACHMENT_LABEL = "Versão anterior da regra"

export async function openRuleAgentSession({ token, signal }) {
    let response = await fetch(RULE_AGENT_BASE_URL + "/session", {
        method: "POST",
        headers: {
            "Authorization": "Bearer " + token
        },
        signal
    })
    if (!response.ok) {
        throw new Error("Failed to open rule agent session: " + response.status)
    }
    let data = await response.json()
    return data.id
}

export function cancelRuleAgentTurn({ sessionId, token }) {
    return fetch(RULE_AGENT_BASE_URL + "/session/" + sessionId + "/turn/cancel", {
        method: "POST",
        headers: {
            "Authorization": "Bearer " + token
        }
    }).catch(() => {})
}

export function buildRuleChangePrompt() {
    return (
        "Você está analisando uma alteração feita em uma regra do QI Tech Rule Engine que o usuário está prestes a salvar.\n\n" +
        "A versão ATUAL da regra (após a alteração) está na seção de contexto desta mensagem. " +
        "A versão ANTERIOR (antes da alteração) está no trecho destacado \"" + PREVIOUS_RULE_ATTACHMENT_LABEL + "\".\n\n" +
        "Explique em português o que mudou entre as duas versões, estruturando sua resposta assim:\n" +
        "1. Um resumo curto (1 a 3 frases) do que mudou e do impacto no comportamento da regra — visível de imediato.\n" +
        "2. Uma tabela de alterações em markdown (colunas: Validação | O que mudou | Antes | Depois; use \"—\" quando um lado não existir). Uma linha por alteração concreta, células curtas.\n" +
        "3. Somente se houver uma explicação longa e detalhada, coloque-a dentro de um bloco :::detalhes Explicação detalhada ... ::: (o dashboard renderiza como seção expansível). Se a explicação for curta, não use o bloco.\n\n" +
        "Se identificar problemas na regra resultante, além de citá-los no texto, reporte-os com a ferramenta report_rule_warnings (severity 'critical' para bugs que quebram a regra ou geram comportamento perigoso; 'warning' para possíveis problemas ou riscos). Não chame a ferramenta se a regra estiver correta.\n\n" +
        "IMPORTANTE: primeiro conclua todas as chamadas de ferramentas, sem escrever texto junto delas; depois escreva a explicação COMPLETA em uma única mensagem final. Texto escrito antes ou entre ferramentas é descartado e nunca chega ao usuário.\n\n" +
        "Ao final da sua resposta, inclua obrigatoriamente uma última linha (fora de qualquer bloco :::detalhes), exatamente no seguinte formato:\n" +
        RESUMO_ALTERACAO_SENTINEL + " <resumo da alteração em uma frase, adequado para um changelog>"
    )
}

export function buildNewRulePrompt() {
    return (
        "Você está analisando uma NOVA regra do QI Tech Rule Engine que o usuário acabou de montar e está prestes a salvar pela primeira vez (a regra está na seção de contexto desta mensagem).\n\n" +
        "Explique em português o que a regra faz e aponte possíveis problemas (validações mortas, decisões faltando, inconsistências, ordem incorreta), estruturando sua resposta assim:\n" +
        "1. Um resumo curto (1 a 3 frases) do que a regra faz — visível de imediato.\n" +
        "2. Problemas encontrados, se houver — sempre visíveis, nunca dentro de bloco expansível.\n" +
        "3. Somente se a explicação validação por validação for longa, coloque-a dentro de um bloco :::detalhes Explicação detalhada ... ::: (o dashboard renderiza como seção expansível). Se for curta, não use o bloco.\n\n" +
        "Se identificar problemas, além de citá-los no texto, reporte-os com a ferramenta report_rule_warnings (severity 'critical' para bugs que quebram a regra ou geram comportamento perigoso; 'warning' para possíveis problemas ou riscos). Não chame a ferramenta se a regra estiver correta.\n\n" +
        "IMPORTANTE: primeiro conclua todas as chamadas de ferramentas, sem escrever texto junto delas; depois escreva a explicação COMPLETA em uma única mensagem final. Texto escrito antes ou entre ferramentas é descartado e nunca chega ao usuário.\n\n" +
        "Ao final da sua resposta, inclua obrigatoriamente uma última linha (fora de qualquer bloco :::detalhes), exatamente no seguinte formato:\n" +
        RESUMO_ALTERACAO_SENTINEL + " <descrição do que a regra faz em uma frase, adequada para o campo de descrição da regra>"
    )
}

export function buildCompanyEventPayload(rulesContext) {
    if (!rulesContext) return null
    return {
        boxes: rulesContext.boxes || {},
        field_mapping: { mappings: rulesContext.fields_mapper_list || [] },
        processors_variables: rulesContext.processors_variables || [],
        signals: rulesContext.signals_list || [],
        scripts: rulesContext.scripts_list || [],
        lists: (rulesContext.lists || []).map((list) => ({
            list_key: list.list_key,
            name: list.name,
            fields: (list.fields || []).map((field) => ({
                name: field.name,
                type: (field.type || {}).enum
            }))
        }))
    }
}

export async function streamRuleChangeTurn({ sessionId, token, prompt, ruleContext, companyEvent, attachments, signal, onDelta, onRule, onWarnings, onDone, onError }) {
    let localController = new AbortController()
    let timedOut = false

    let forwardAbort = () => localController.abort()
    if (signal) {
        if (signal.aborted) localController.abort()
        else signal.addEventListener("abort", forwardAbort)
    }

    let timeoutId = null
    let armIdleTimeout = () => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
            timedOut = true
            localController.abort()
        }, RULE_AGENT_STREAM_IDLE_TIMEOUT_MS)
    }
    armIdleTimeout()

    let cleanup = () => {
        clearTimeout(timeoutId)
        if (signal) signal.removeEventListener("abort", forwardAbort)
    }

    let payload = { prompt }
    if (ruleContext) payload.rule_context = ruleContext
    if (companyEvent) payload.company_event = companyEvent
    if (attachments && attachments.length > 0) payload.attachments = attachments

    try {
        let response = await fetch(RULE_AGENT_BASE_URL + "/session/" + sessionId + "/turn/stream", {
            method: "POST",
            headers: {
                "Authorization": "Bearer " + token,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload),
            signal: localController.signal
        })

        if (!response.ok || !response.body) {
            cleanup()
            onError && onError({ message: "Failed to start rule agent stream: " + response.status })
            return
        }

        let reader = response.body.getReader()
        let decoder = new TextDecoder()
        let buffer = ""

        while (true) {
            let { value, done } = await reader.read()
            if (done) break
            armIdleTimeout()
            buffer += decoder.decode(value, { stream: true })

            let frames = buffer.split("\n\n")
            buffer = frames.pop()

            for (let frame of frames) {
                parseRuleAgentFrame(frame, { onDelta, onRule, onWarnings, onDone, onError })
            }
        }

        if (buffer.trim()) {
            parseRuleAgentFrame(buffer, { onDelta, onRule, onWarnings, onDone, onError })
        }
        cleanup()
    } catch (error) {
        cleanup()
        if (timedOut) {
            onError && onError({ message: "Rule agent stream timed out" })
            return
        }
        if (error.name === "AbortError") return
        onError && onError({ message: error.message })
    }
}

export const streamRuleAgentTurn = streamRuleChangeTurn

export function getRuleAgentDisplayText(fullText) {
    let sentinelIndex = fullText.indexOf(RESUMO_ALTERACAO_SENTINEL)
    if (sentinelIndex !== -1) {
        return fullText.slice(0, sentinelIndex).trim()
    }

    let maxOverlap = Math.min(RESUMO_ALTERACAO_SENTINEL.length - 1, fullText.length)
    for (let overlap = maxOverlap; overlap > 0; overlap--) {
        if (fullText.slice(-overlap) === RESUMO_ALTERACAO_SENTINEL.slice(0, overlap)) {
            return fullText.slice(0, fullText.length - overlap)
        }
    }

    return fullText
}

export function extractRuleChangeSummary(fullText) {
    let sentinelIndex = fullText.indexOf(RESUMO_ALTERACAO_SENTINEL)
    if (sentinelIndex === -1) return null
    let summary = fullText.slice(sentinelIndex + RESUMO_ALTERACAO_SENTINEL.length).trim()
    return summary.length > 0 ? summary : null
}

function parseRuleAgentFrame(frame, { onDelta, onRule, onWarnings, onDone, onError }) {
    let eventType = "message"
    let dataLines = []

    frame.split("\n").forEach((line) => {
        if (line.startsWith("event:")) {
            eventType = line.slice("event:".length).trim()
        } else if (line.startsWith("data:")) {
            dataLines.push(line.slice("data:".length).trim())
        }
    })

    if (dataLines.length === 0) return

    let rawData = dataLines.join("\n")
    let data
    try {
        data = JSON.parse(rawData)
    } catch (parseError) {
        console.error("Failed to parse rule agent SSE frame data:", rawData, parseError)
        return
    }

    if (eventType === "delta") {
        onDelta && onDelta(data)
    } else if (eventType === "rule") {
        onRule && onRule(data)
    } else if (eventType === "warnings") {
        onWarnings && onWarnings(data)
    } else if (eventType === "done") {
        onDone && onDone(data)
    } else if (eventType === "error") {
        onError && onError(data)
    }
}
